import { createAction, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { extractSharedActions } from '@sda/utils/store';
import { updateCurrentSessionUser } from '@client/slices/helpers/session.reducer';
import { Reducers } from '@sda/enums/store';
import { createEntityAdapter } from '@client/store/entity.adapter';
import { ProfileTypes } from '@sda/enums';
import { getDateTime } from '@sda/utils/date';
import type { ExtendedUser } from '@sda/interfaces/reducer/session.reducer';
import type { GenericList } from '@sda/helpers/helpers.types';
import type {
  ActsState, UpdateActPayload, VanityUrlPayload, CreateActPayload,
} from '@sda/interfaces/reducer/acts.reducer';
import type { Act, PublicAct } from '@sda/interfaces/act';
import type { FetchDataType } from '@sda/sdk/interfaces';

// LISTENERS

export const startCreatePartialAct = createAction<Pick<CreateActPayload, 'compiledAddress' | 'latLng' | 'name' | 'googlePlaceId'>>(`${Reducers.ACTS}/startCreatePartialAct`);
export const startCreateAct = createAction<CreateActPayload>(`${Reducers.ACTS}/startCreateAct`);
export const startPatchAct = createAction<CreateActPayload>(`${Reducers.ACTS}/startPatchAct`);
export const startUpdateAct = createAction<UpdateActPayload>(`${Reducers.ACTS}/startUpdateAct`);
export const startUpdateActVanityUrl = createAction<VanityUrlPayload>(`${Reducers.ACTS}/startUpdateActVanityUrl`);

/**
 * data fetching
 */
export const startFetchFullAct = createAction<string>(`${Reducers.ACTS}/startFetchFullAct`);
export const startFetchPublicActDetails = createAction<string>(`${Reducers.ACTS}/startFetchPublicActDetails`);
export const unsubscribeAct = createAction<string>(`${Reducers.ACTS}/unsubscribeAct`);

export const actAdapter = createEntityAdapter<Act & FetchDataType>({
  sortComparer: (a, b) => a.name?.localeCompare(b.name) || getDateTime(b.createdAt).toMillis() - getDateTime(a.createdAt).toMillis(),
});

const initialState: ActsState = {
  selectedProfileId: null,
};

const actsSlice = createSlice({
  initialState: actAdapter.getInitialState<ActsState>(initialState),
  name: Reducers.ACTS,
  reducers: {
    ...extractSharedActions(actAdapter),
    upsertPublicAct: (state, action: PayloadAction<PublicAct & FetchDataType>) => {
      state.status = true;

      actAdapter.upsertOne(state, action.payload as Act & FetchDataType);
    },
    upsertPublicActs: (state, action: PayloadAction<GenericList<PublicAct & FetchDataType>>) => {
      state.status = true;

      actAdapter.upsertMany(state, action.payload as GenericList<Act & FetchDataType>);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCurrentSessionUser, (state, action: PayloadAction<ExtendedUser>) => {
        if (action.payload?.selectedProfileId) {
          state.selectedProfileId = action.payload?.selectedProfileType === ProfileTypes.ACT ? action.payload.selectedProfileId : null;
        }
      });
  },
});

export const {
  upsertOne: upsertAct, updateOne: updateAct, upsertPublicAct, upsertPublicActs,
} = actsSlice.actions;

export default actsSlice.reducer;
